import { useEffect, useRef, useState } from "react";

type BootInput = {
  /** 3D 场景是否已结束加载（成功或失败都算结束）。 */
  sceneSettled: boolean;
  /** 首页数据请求是否已结束。 */
  dataSettled: boolean;
};

const MIN_MS = 1200;
const MAX_MS = 3500;

/** 同一次浏览会话里只在首次进入时播放加载页，站内返回首页不再重复。 */
let bootedThisSession = false;

/**
 * 首页进场加载：等待字体、首页数据与 3D 场景，至少停留 MIN_MS 以完成进场节奏，
 * 最多等待 MAX_MS，超时后直接进入页面，未完成的内容各自显示加载状态。
 */
export function useHomeBoot({ sceneSettled, dataSettled }: BootInput) {
  const [active] = useState(() => !bootedThisSession);
  const [fontsReady, setFontsReady] = useState(false);
  const [elapsedOk, setElapsedOk] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [progress, setProgress] = useState(active ? 0 : 100);
  const [finished, setFinished] = useState(!active);
  const targetRef = useRef(0);

  useEffect(() => {
    if (!active) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const minTimer = window.setTimeout(() => setElapsedOk(true), reduced ? 300 : MIN_MS);
    const maxTimer = window.setTimeout(() => setTimedOut(true), MAX_MS);
    let cancelled = false;
    (document.fonts?.ready ?? Promise.resolve()).then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
      window.clearTimeout(minTimer);
      window.clearTimeout(maxTimer);
    };
  }, [active]);

  const allReady = (fontsReady && dataSettled && sceneSettled && elapsedOk) || timedOut;
  targetRef.current = allReady ? 100 : 8 + Number(fontsReady) * 24 + Number(dataSettled) * 30 + Number(sceneSettled) * 30;

  useEffect(() => {
    if (!active || finished) return;
    let frame = 0;
    const tick = () => {
      setProgress((value) => {
        const next = value + Math.max((targetRef.current - value) * 0.08, targetRef.current > value ? 0.25 : 0);
        return Math.min(next, targetRef.current);
      });
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [active, finished]);

  useEffect(() => {
    if (!active || finished || progress < 100) return;
    const timer = window.setTimeout(() => {
      bootedThisSession = true;
      setFinished(true);
    }, 240);
    return () => window.clearTimeout(timer);
  }, [active, finished, progress]);

  return { active, progress: Math.round(progress), finished };
}
